/**
 * Card Library previews for the subscriptions page — see cards/previews.tsx contract.
 *
 * The section components (SubMoneyCard, CancelCurveCard, …) are the SINGLE
 * source of the card JSX: SubscriptionsPage imports them back, and the
 * no-prop preview wrappers below run the same queries (same keys — TanStack
 * dedupes) and feed them, so the library renders cards pixel-identical to
 * their home page.
 */

import { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useSetupGate } from "../lib/setup-gate.js";
import { usePeriod } from "../lib/period.js";
import { Card, CardHead, Tile, moneyFmt } from "../components/cards.js";
import { useDrill } from "../lib/drill.js";
import { TrendChart } from "../components/TrendChart.js";
import { useMetricTrend, type TrendPt } from "../components/CardTrend.js";
import { RevenueCmToggle, useStickyRevenueCmMode } from "../components/RevenueCmToggle.js";

/** Period-scoped net-adds spark: (new − canceled) per bucket over the picker
 *  window, aligned by label (a bucket may have new but no cancels, or vice
 *  versa). Returns the values in chronological order, or undefined until the
 *  series load. Feeds the Net adds tile so its mini-trend moves with the picker
 *  like its headline (the fixed trailing-12-month `trend` stays for the exempt
 *  "New vs canceled by month" card, which must NOT follow the window). */
function netAddsSpark(newT?: TrendPt[], canceledT?: TrendPt[]): number[] | undefined {
  if (!newT && !canceledT) return undefined;
  const byLabel = new Map<string, number>();
  for (const p of newT ?? []) byLabel.set(p.label, (byLabel.get(p.label) ?? 0) + p.value);
  for (const p of canceledT ?? []) byLabel.set(p.label, (byLabel.get(p.label) ?? 0) - p.value);
  return [...byLabel.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([, v]) => v);
}

/** Product-type filter for the retention views (S4). */
export type SubTypeFilter = "all" | "physical" | "app";

export interface SubRetention {
  curve: { weeks: Array<{ week: number; count: number; atRisk: number; hazardPct: number }>; spikeWeeks: number[]; totalCanceled: number };
  cohorts: Array<{ cohort: string; size: number; activePctByMonth: Array<number | null> }>;
}

export interface SubTypeRow {
  type: string; label: string; activeSubs: number; recurringValue: number; recurringValueCm: number | null;
  recurringValueCmCoverage: number | null; recurringValueCmEstimated: boolean;
  monthlyChurnPct: number | null; avgDaysToCancel: number | null; totalSubs: number;
}

export interface SubscriptionSourceCoverage {
  planState: {
    /** Final source re-read. Optional only so a new web bundle fails closed
     * against an older response during an independent deploy. */
    available?: boolean;
    scope: "provider_only" | "account";
    providers: Array<{ platform: string; label: string; plans: number; activePlans: number }>;
    totalPlans: number;
    activePlans: number;
  };
  scheduledDates: { knownPlans: number; eligiblePlans: number; coverage: number | null };
  recurringSales: {
    /** Final source re-read; absent is unavailable during rollout. */
    available?: boolean;
    scope: "account_recurring_orders";
    providers: Array<{
      platform: string;
      label: string;
      orders: number;
      revenue: number;
      /** Latest recurring-order date imported from this source. */
      dataThrough?: string | null;
      /** True when this source comes from a fixed historical import, not a proven live feed. */
      historicalOnly?: boolean;
    }>;
  };
  orderLinkage: { linkedPlans: number; eligiblePlans: number; coverage: number | null; reliable: boolean };
  forecastCompatible: boolean;
}

export interface SubResponse {
  summary: {
    activeSubs: number; canceledSubs: number; endedSubs: number; disabledSubs: number; restartedSubs: number; totalSubs: number;
    everSubscribedPct: number | null; everSubscribedCustomers: number; everSubscribedLinkCoverage: number | null;
    recurringValue: number; recurringValueCm: number | null; avgDaysToCancel: number | null;
    recurringValueCmCoverage: number | null; recurringValueCmEstimated: boolean;
    new30: number; ended30: number; monthlyChurnPct: number | null;
    windowChurnPct: number | null; churnWindowDays: number; netAdds30: number;
    upcomingRebillCount30: number; upcomingRebillValue30: number; upcomingRebillValueCm30: number | null;
    upcomingRebillValueCmCoverage: number | null; upcomingRebillValueCmEstimated: boolean;
  };
  leaks: {
    involuntary: { plans: number; revenue: number; cm: number | null; cmCoverage: number | null; cmEstimated: boolean };
    voluntary: { plans: number; revenue: number; cm: number | null; cmCoverage: number | null; cmEstimated: boolean };
    recoverableNow: { plans: number; revenue: number; cm: number | null; cmCoverage: number | null; cmEstimated: boolean };
  };
  subscriptionValue: {
    cohortPlans: number;
    endedPlans: number;
    datedEndedPlans?: number;
    avgRevenue12: number | null;
    avgCm12: number | null;
    avgStickMonths12: number | null;
    revenueCaptureDays90: number | null;
    cmCaptureDays90: number | null;
    linkCoverage: number | null;
    rebillCountCoverage: number | null;
    sourceValueCoverage: number | null;
    cmCoverage: number | null;
    cmEstimated: boolean;
    stickCoverage: number | null;
  };
  trend: Array<{ month: string; newSubs: number; endedSubs: number; incomplete?: boolean }>;
  churnTrend: Array<{ month: string; churnPct: number | null; canceled: number; base: number; incomplete?: boolean }>;
  cancelAges: Array<{ bucket: string; count: number }>;
  byType: SubTypeRow[];
  rebills: { actual30: number; actualCm30: number | null; orders30: number; projected30: number | null; projectedCm30: number | null; cmRatio: number | null; cmCoverage: number | null };
  /** Source boundaries for plan-only and order-only subscription results. Older
   * responses omit this, so the new split page must fail closed without it. */
  sourceCoverage?: SubscriptionSourceCoverage;
  /** False when the account has no computed margin (costs not set up) → the page
   *  falls back to Revenue, since CM reads as negative garbage. */
  hasCm?: boolean;
}

type SubSummary = SubResponse["summary"];

const pct = (n: number | null): string => (n === null ? "—" : `${(n * 100).toFixed(1)}%`);

/** Same query (and key) as SubscriptionsPage — mounting a preview is free when the page is cached. */
export function useSubsQuery(sourceReady = true): UseQueryResult<SubResponse, Error> {
  const api = useApiClient();
  const gate = useSetupGate();
  const period = usePeriod();
  const ready = gate.data?.previewAllowed === true && gate.data.syncStatus === "ready";
  return useQuery({
    // Movement metrics (new/canceled/net adds) follow the window; the base + churn
    // + rebill book stay point-in-time server-side (founder 2026-07-01 hybrid).
    queryKey: ["subscriptions", period.cacheKey],
    queryFn: () => api.get<SubResponse>("/dashboard/subscriptions", { ...period.query }),
    enabled: ready && sourceReady,
  });
}

export function useSubRetentionQuery(subType: SubTypeFilter = "all", sourceReady = true): UseQueryResult<SubRetention, Error> {
  const api = useApiClient();
  const gate = useSetupGate();
  const ready = gate.data?.previewAllowed === true && gate.data.syncStatus === "ready";
  return useQuery({
    queryKey: ["sub-retention", subType],
    queryFn: () => api.get<SubRetention>("/dashboard/sub-retention", subType === "all" ? {} : { subType }),
    enabled: ready && sourceReady,
  });
}

/** Use the provider's own label anywhere a plan metric appears. */
export function subscriptionPlanSourceLabel(coverage: SubscriptionSourceCoverage | undefined): string {
  const labels = [...new Set((coverage?.planState.providers ?? []).map((provider) => provider.label.trim()).filter(Boolean))];
  return labels.length === 1 ? labels[0]! : "Subscription source";
}

/* ───────────────────────── section components (page + preview) ───────────── */

/** Actual recurring-order sales. This never borrows plan lifecycle facts. */
export function RecurringSalesCard({
  rb,
  coverage,
  style,
}: {
  rb: SubResponse["rebills"];
  coverage: SubscriptionSourceCoverage["recurringSales"];
  style?: React.CSSProperties;
}): React.ReactElement {
  const drill = useDrill();
  const period = usePeriod();
  const providerNames = coverage.providers.map((provider) => provider.label).join(" and ");
  const historicalSources = coverage.providers.filter((provider) => provider.historicalOnly === true);
  const partial = historicalSources.length > 0;
  return (
    <Card
      onClick={() => drill.open({ title: "Recurring sales from orders", metric: "rebill_revenue", dimension: "month", period: period.drillWindow })}
      style={style}
    >
      <CardHead title={`Recurring sales from orders · ${period.label}`} explain={[
        `<b>What it is:</b> Product revenue from ${rb.orders30.toLocaleString()} orders that connected order sources marked as recurring during ${period.label.toLowerCase()}.`,
        "<b>How it's calculated:</b> Add recurring-order product revenue after recorded refunds. The original sign-up order is not counted as a rebill.",
        `<b>Sources:</b> ${providerNames || "Connected order sources"}. These are order records. They are not treated as plan status or future charges.`,
      ]} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginTop: 14 }}>
        <MiniStat label={partial ? "Product revenue (partial)" : "Product revenue"} value={moneyFmt(rb.actual30)} sub={`${rb.orders30.toLocaleString()} recurring orders${partial ? " · partial history" : ""}`} />
        <MiniStat label="Order sources" value={coverage.providers.length.toLocaleString()} sub={providerNames || "No source names returned"} />
      </div>
      {partial && (
        <div
          role="note"
          aria-label="Partial recurring-sales history"
          style={{ marginTop: 14, padding: "10px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--warn, #b5820a) 9%, var(--surface))", border: "1px solid color-mix(in srgb, var(--warn, #b5820a) 45%, var(--border))", color: "var(--text)", fontSize: 12.5, lineHeight: 1.5 }}
        >
          {historicalSources.map((provider) => {
            const date = formatRecurringDataThrough(provider.dataThrough);
            return (
              <div key={provider.platform}>
                {date
                  ? `${provider.label} recurring history currently runs through ${date}. Sales after that date may be missing.`
                  : `${provider.label} recurring history comes from a historical import. Recent sales may be missing.`}
              </div>
            );
          })}
          <div style={{ fontWeight: 700, marginTop: 3 }}>The total above is partial.</div>
        </div>
      )}
      {coverage.providers.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--hair)", display: "flex", flexWrap: "wrap", gap: "8px 18px", color: "var(--body)", fontSize: 12 }}>
          {coverage.providers.map((provider) => (
            <span key={provider.platform}>
              <b style={{ color: "var(--text)" }}>{provider.label}</b>: {provider.orders.toLocaleString()} orders, {moneyFmt(provider.revenue)}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

function formatRecurringDataThrough(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00.000Z` : value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/** Future charges from the plan provider, with the missing-date population stated. */
export function UpcomingChargesCard({
  s,
  coverage,
  sourceLabel,
}: {
  s: SubSummary;
  coverage: SubscriptionSourceCoverage["scheduledDates"];
  sourceLabel: string;
}): React.ReactElement {
  const missing = Math.max(0, coverage.eligiblePlans - coverage.knownPlans);
  const coveragePct = coverage.coverage === null ? "not applicable" : `${(coverage.coverage * 100).toFixed(1)}%`;
  const coverageCopy = coverage.eligiblePlans === 0
    ? `There are no active ${sourceLabel} plans that need a next charge date.`
    : missing === 0
      ? `All ${coverage.eligiblePlans.toLocaleString()} active ${sourceLabel} plans have a known next charge date.`
      : `${coverage.knownPlans.toLocaleString()} of ${coverage.eligiblePlans.toLocaleString()} active ${sourceLabel} plans have a known next charge date (${coveragePct}). ${missing.toLocaleString()} active plans are missing a date, so this is a partial total.`;
  return (
    <Card style={{ marginBottom: 0 }}>
      <CardHead title={`${sourceLabel} upcoming charges · next 30 days`} explain={[
        `<b>What it is:</b> Charges currently scheduled in ${sourceLabel} for the next 30 days.`,
        "<b>How it's calculated:</b> Add the next charge amount for active plans whose next charge date falls in the coming 30 days.",
        `<b>Coverage:</b> ${coverageCopy}`,
        `<b>Source:</b> ${sourceLabel} plan records only. This is not a company-wide forecast and it is not combined with recurring-order sales.`,
      ]} />
      <div style={{ marginTop: 14 }}>
        <MiniStat
          label="Scheduled amount"
          value={moneyFmt(s.upcomingRebillValue30)}
          sub={`${s.upcomingRebillCount30.toLocaleString()} ${sourceLabel} plans scheduled in the next 30 days`}
        />
      </div>
      <div role="note" style={{ marginTop: 14, color: missing > 0 ? "var(--warn, #b5820a)" : "var(--body)", fontSize: 12.5, lineHeight: 1.5 }}>
        {coverageCopy}
      </div>
    </Card>
  );
}

/** Only appears when the API proves plan and order populations are compatible. */
export function SubscriptionProjectionCard({ rb, sourceLabel }: { rb: SubResponse["rebills"]; sourceLabel: string }): React.ReactElement {
  return (
    <Card style={{ marginBottom: 0 }}>
      <CardHead title={`${sourceLabel}-linked recurring-sales forecast · next 30 days`} explain={[
        `<b>What it is:</b> A next-30-day revenue forecast shown only when ${sourceLabel} plans reliably match imported recurring orders.`,
        "<b>How it's calculated:</b> Start with recent recurring-order revenue, reduce it by measured plan churn, and add expected new plans.",
      ]} />
      <div style={{ marginTop: 14 }}>
        <MiniStat label="Projected product revenue" value={moneyFmt(rb.projected30)} sub="Uses linked plan and recurring-order history" />
      </div>
    </Card>
  );
}

/** sub-money-30d — "The 30-day money picture". */
export function SubMoneyCard({ s, rb, cmAvailable = true, style }: { s: SubSummary; rb: SubResponse["rebills"]; cmAvailable?: boolean; style?: React.CSSProperties }): React.ReactElement {
  const drill = useDrill();
  const period = usePeriod();
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-sub-money");
  const churnPctLabel = s.monthlyChurnPct !== null ? (s.monthlyChurnPct * 100).toFixed(1) : null;
  const shownMode = cmAvailable ? mode : "revenue";
  const cm = shownMode === "profit";
  const actual = cm ? rb.actualCm30 : rb.actual30;
  const projected = cm ? rb.projectedCm30 : rb.projected30;
  const scheduled = cm ? s.upcomingRebillValueCm30 : s.upcomingRebillValue30;
  const actualSub = cm && rb.actual30 > 0 && actual === null
    ? "Needs costs for these rebill orders"
    : cm && rb.cmCoverage !== null && rb.cmCoverage < 0.999
      ? `Costs are complete for ${(rb.cmCoverage * 100).toFixed(1)}% of rebill revenue`
    : rb.actual30 > 0 ? `${rb.orders30.toLocaleString()} rebill orders` : "Filling in";
  const projectedSub = cm && rb.actual30 > 0 && projected === null
    ? "Needs complete rebill costs"
    : churnPctLabel !== null ? `× (1 − ${churnPctLabel}% churn)` : "Needs churn history";
  const scheduledSub = cm && scheduled === null
    ? "Plan costs are not linked yet"
    : cm && s.upcomingRebillValueCmEstimated
      ? `${s.upcomingRebillCount30.toLocaleString()} shipments · CM estimated from recent rebills`
    : `${s.upcomingRebillCount30.toLocaleString()} shipments on the calendar`;
  return (
    <Card onClick={() => drill.open({ title: cm ? "Subscription CM" : "Subscription revenue", metric: cm ? "rebill_cm" : "rebill_revenue", dimension: "month", period: period.drillWindow })} style={{ marginBottom: 16, ...style }}>
      <CardHead title={`Subscription summary · ${period.label}`} explain={[
        `<b>What it is:</b> A summary of your subscription program: what it actually collected over ${period.label.toLowerCase()}, what it's likely to collect next month, and what's already booked on the shipment calendar. All three numbers use the same Revenue or CM basis, so they line up.`,
        "<b>How it's calculated:</b> Actual = real money from rebill orders that billed in the selected window. Projected = last-30-days actual reduced by your total monthly churn rate, plus expected new subscribers times a typical rebill. Scheduled = active plans with a shipment date in the next 30 days at their per-cycle price.",
        "<b>Example (sample data · last 30 days, Revenue view):</b> Actual = $79,476 (1,317 rebill orders). Projected = $82,141. Scheduled = $62,250 (979 shipments on the calendar). On the CM view: Actual = $42,230, Projected = $43,646, Scheduled = $33,077.",
        "<b>Note:</b> A subscription is one recurring plan from your connected source. \"Actual\" counts recurring rebills, not the sign-up order. <b>Actual follows the date picker; Projected and Scheduled are always the next 30 days.</b> Projected stays blank until a real churn rate exists. On the CM view, Scheduled uses the margin for the plans actually on that calendar. If we cannot match plan costs or use a recent rebill margin, the number stays blank.",
      ]}><RevenueCmToggle mode={shownMode} setMode={setMode} cmDisabled={!cmAvailable} /></CardHead>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14, marginTop: 12 }}>
        {[
          { label: `Actual · ${period.label}`, v: rb.actual30 > 0 ? actual : null, sub: actualSub },
          { label: "Projected · next 30 days", v: rb.actual30 > 0 ? projected : null, sub: projectedSub },
          { label: "Scheduled · next 30 days", v: scheduled, sub: scheduledSub },
        ].map((m) => (
          <div key={m.label}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--faint)" }}>{m.label}</div>
            <div className="dd-num" style={{ fontSize: 23, fontWeight: 700, color: "var(--num)", marginTop: 4 }}>{m.v === null ? "—" : moneyFmt(m.v)}</div>
            <div style={{ fontSize: 11.5, color: "var(--body)", marginTop: 2 }}>{m.sub}</div>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 10, paddingBottom: 4 }} aria-hidden="true">
          {(() => {
            const vals = [rb.actual30 > 0 ? (actual ?? 0) : 0, rb.actual30 > 0 ? (projected ?? 0) : 0, scheduled ?? 0];
            const mx = Math.max(1, ...vals);
            const tones = ["var(--accent)", "color-mix(in srgb, var(--accent) 65%, var(--surface))", "color-mix(in srgb, var(--accent) 35%, var(--surface))"];
            return vals.map((v, i) => (
              <div key={i} style={{ width: 26, height: `${Math.max(6, (v / mx) * 64)}px`, background: tones[i], borderRadius: 5 }} />
            ));
          })()}
        </div>
      </div>
    </Card>
  );
}

/** net-adds — follows the selected window (movement metric). */
export function NetAddsTile({ s, sourceLabel = "Subscription" }: { s: SubSummary; sourceLabel?: string }): React.ReactElement {
  const period = usePeriod();
  // The mini-trend now moves with the picker like the headline: (new − canceled)
  // per bucket over the selected window, from the same period-scoped breakdown
  // the drills use. (Was the fixed trailing-12-month `trend` array, which stayed
  // put no matter the range — now unused here; still feeds the exempt monthly card.)
  const newTrend = useMetricTrend("subs_new");
  const endedTrend = useMetricTrend("subs_ended");
  const spark = netAddsSpark(newTrend, endedTrend);
  return (
    <Tile title={`${sourceLabel} plan net adds · ${period.label}`} value={`${s.netAdds30 >= 0 ? "+" : "−"}${Math.abs(s.netAdds30).toLocaleString()}`} sub={`${s.new30.toLocaleString()} new · ${s.ended30.toLocaleString()} ended`}
      spark={spark}
      preferSpark
      explain={[
        `<b>What it is:</b> How much the ${sourceLabel} plan base grew or shrank over the selected dates.`,
        `<b>How it's calculated:</b> ${sourceLabel} plans started in the selected window minus plans that source recorded as ended. A plan with more than one end event counts once, on the first date it stopped.`,
        "<b>Example:</b> −338 means the base shrank by 338 over the window: 0 new and 338 ended.",
        "<b>Note:</b> This follows your date picker. The small trend uses the same new-minus-ended math in every period shown. Open Subscription leaks to see how much came from failed payments versus customer cancellations.",
      ]} />
  );
}

/** monthly-churn — "Churn rate" tile. Now FOLLOWS the date picker (founder 2026-07-15):
 *  the share of the base live at the window's start that canceled during it. */
export function MonthlyChurnTile({ s, sourceLabel = "Subscription" }: { s: SubSummary; sourceLabel?: string }): React.ReactElement {
  const period = usePeriod();
  // "All time" (and any window that opens before the first plan existed) has no base
  // to have been "live at the start," so windowChurnPct is null — fall back to the
  // lifetime rate (every plan ever started that has since canceled), the meaningful
  // all-time churn, with a matching sub-line.
  const useLifetime = s.windowChurnPct === null;
  const lifetime = s.totalSubs > 0 ? s.endedSubs / s.totalSubs : null;
  const shown = useLifetime ? lifetime : s.windowChurnPct;
  const sub = useLifetime
    ? `of all ${s.totalSubs.toLocaleString()} plans ever started`
    : "of the base live when the window opened";
  return (
    <Tile title={`${sourceLabel} plan churn · ${period.label}`} value={pct(shown)} sub={sub}
      explain={[
        `<b>What it is:</b> Of the ${sourceLabel} plans live when this window opened, the share that ended.`,
        `<b>How it's calculated:</b> ${sourceLabel} plans from the opening base whose first recorded end date falls inside the selected window, divided by plans live when the window opened. Plans that both start and end inside the window belong in Net adds, not this rate.`,
        "<b>Example (sample data · 90 days):</b> 12.1% means roughly 12 of every 100 plans that were live 90 days ago have ended since.",
        "<b>Note:</b> A wider window usually shows more total churn, but it begins with an older, more established base. Open Subscription leaks to split failed payments from customer cancellations.",
      ]} />
  );
}

/** active-subs — "Active subscriptions" tile. */
export function ActiveSubsTile({ s, sourceLabel = "Subscription" }: { s: SubSummary; sourceLabel?: string }): React.ReactElement {
  return (
    <Tile title={`Active ${sourceLabel} plans · right now`} value={s.activeSubs.toLocaleString()}
      sub={s.restartedSubs > 0 ? `incl. ${s.restartedSubs.toLocaleString()} won back after canceling` : s.disabledSubs > 0 ? `+${s.disabledSubs.toLocaleString()} paused/disabled (recoverable)` : `${s.totalSubs.toLocaleString()} ever`}
      explain={[
        `<b>What it is:</b> How many plans ${sourceLabel} marks active right now. This is the ${sourceLabel} base, not a company-wide total.`,
        `<b>How it's calculated:</b> A count of ${sourceLabel} plans whose current status is active.`,
        "<b>Example (sample data · current):</b> 3,186 active. Sub-line reads \"5,280 ever\" (no won-back or disabled plans, so it falls back to total plans started).",
        "<b>Note:</b> A subscription is one recurring plan from your connected source. \"Won back\" means a plan canceled and later restarted. \"Disabled\" means a paused plan or failed payment that may still be recovered.",
      ]} />
  );
}

/** subscription-leaks — voluntary vs failed-payment churn, with the money that
 *  can still be recovered right now. CM is available only when plan records
 *  reliably match imported orders with complete costs. */
export function SubscriptionLeaksCard({ leaks, cmAvailable = true, sourceLabel = "Subscription" }: { leaks: SubResponse["leaks"]; cmAvailable?: boolean; sourceLabel?: string }): React.ReactElement {
  const period = usePeriod();
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-sub-leaks");
  const shownMode = cmAvailable ? mode : "revenue";
  const cm = shownMode === "profit";
  const value = (b: SubResponse["leaks"]["involuntary"]): number | null => cm ? b.cm : b.revenue;
  const bucketSub = (b: SubResponse["leaks"]["involuntary"], label: string): string =>
    cm && b.cm === null
      ? "Plan costs are not linked yet"
      : cm && b.cmEstimated
        ? `${b.plans.toLocaleString()} plans · CM estimated from recent rebills`
        : label;
  return (
    <Card style={{ marginBottom: 0 }}>
      <CardHead title={`${sourceLabel} plan losses · ${period.label}`} explain={[
        `<b>What it is:</b> Why ${sourceLabel} plans ended over ${period.label.toLowerCase()}, plus the failed-payment amount ${sourceLabel} still marks recoverable.`,
        `<b>How it's calculated:</b> When ${sourceLabel} says payment retries failed, we count that as a failed payment. Other cancellations count as customer cancellations. Each dollar figure is the plan's next normal charge value.`,
        "<b>Example (sample data):</b> 31 plans failed because of payment problems and represented $7,800 in next-rebill revenue. 43 customers canceled and represented $8,540. Of the failed plans, $4,900 was still recoverable right now.",
        `<b>Source limit:</b> These are ${sourceLabel} plan records only. Product, SKU, and contribution-margin breakdowns require reliable links to imported orders.`,
      ]}><RevenueCmToggle mode={shownMode} setMode={setMode} cmDisabled={!cmAvailable} /></CardHead>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginTop: 14 }}>
        <MiniStat label="Failed payments" value={moneyFmt(value(leaks.involuntary))} sub={bucketSub(leaks.involuntary, `${leaks.involuntary.plans.toLocaleString()} plans · next rebill value`)} />
        <MiniStat label="Customer cancellations" value={moneyFmt(value(leaks.voluntary))} sub={bucketSub(leaks.voluntary, `${leaks.voluntary.plans.toLocaleString()} plans · next rebill value`)} />
        <MiniStat label="Recoverable now" value={moneyFmt(value(leaks.recoverableNow))} sub={bucketSub(leaks.recoverableNow, `${leaks.recoverableNow.plans.toLocaleString()} failed-payment plans`)} />
      </div>
    </Card>
  );
}

/** subs-new-vs-canceled — "New vs canceled subscriptions by month" chart card. */
export function SubsNewVsCanceledCard({ trend, sourceLabel = "Subscription" }: { trend: SubResponse["trend"]; sourceLabel?: string }): React.ReactElement {
  // Drop the still-forming month the API flags (the current month for a live
  // account, or the frozen partial month for the demo): a half-month of new plans
  // against a full month of cancels reads as a false cliff (founder 2026-07-15).
  const shown = trend.filter((t) => !t.incomplete);
  return (
    <Card>
      <CardHead title={`${sourceLabel} plan starts vs ends by month`} explain={[
        `<b>What it is:</b> A month-by-month view of ${sourceLabel} plan starts and endings.`,
        `<b>How it's calculated:</b> For each complete month, count ${sourceLabel}-recorded plan starts and first endings. A plan with more than one end event counts once. The still-forming month is left off.`,
        "<b>Example (sample data):</b> 301 new versus 97 ended is a gain of 204 plans for the month.",
        "<b>Note:</b> Open Subscription leaks to split failed payments from customer cancellations. The current month stays hidden until it is complete, so a partial month cannot look like a sudden collapse.",
      ]} />
      <div style={{ marginTop: 12 }} onClick={(e) => e.stopPropagation()}>
        <TrendChart kind="line" switchKinds={["line", "grouped-bars", "stacked-bars", "table"]} chartKey="subs-new-vs-canceled" money={false} seriesNames={["New", "Ended"]} seriesBColor="var(--bad)"
          points={shown.map((t) => ({ label: t.month, a: t.newSubs, b: t.endedSubs }))} />
      </div>
      <div style={legend}>
        <span><i style={dot("var(--accent)")} />New</span>
        <span><i style={dot("var(--bad)")} />Ended</span>
        {/* Net gains across the window — the "is the base growing or shrinking?"
            read the founder asked for (2026-07-06). New minus canceled, summed. */}
        {shown.length > 0 && (() => {
          const net = shown.reduce((s, t) => s + (t.newSubs - t.endedSubs), 0);
          return (
            <span style={{ marginLeft: "auto", fontWeight: 700, color: net >= 0 ? "var(--good)" : "var(--bad)" }}
              title="New minus ended across every month shown">
              Net {net >= 0 ? "▲ +" : "▼ −"}{Math.abs(net).toLocaleString()} over {shown.length} mo
            </span>
          );
        })()}
      </div>
    </Card>
  );
}

/** churn-rate-trend — "Churn rate by month" line (founder 2026-07-15, Praxis parity). */
export function ChurnRateByMonthCard({ churnTrend, sourceLabel = "Subscription" }: { churnTrend: SubResponse["churnTrend"]; sourceLabel?: string }): React.ReactElement {
  // Drop the still-forming last month: a part-month of cancels against a full base
  // reads erratically (same rule as New-vs-canceled).
  const shown = churnTrend.filter((t) => !t.incomplete && t.churnPct !== null);
  const avg = shown.length ? shown.reduce((sum, t) => sum + (t.churnPct ?? 0), 0) / shown.length : null;
  return (
    <Card>
      <CardHead title={`${sourceLabel} plan churn by month`} explain={[
        `<b>What it is:</b> ${sourceLabel} plan churn month by month, including customer cancellations and failed payments recorded there.`,
        `<b>How it's calculated:</b> For each complete month, ${sourceLabel} plans from the opening base that ended during the month divided by the plans live when the month began. A plan with more than one end event counts once.`,
        "<b>Example (sample data):</b> 4% means about 4 of every 100 plans live at the start of that month ended before it closed.",
        "<b>Note:</b> The current month stays hidden until it is complete. Open Subscription leaks to see the failed-payment and customer-cancellation split.",
      ]} />
      <div style={{ marginTop: 12 }} onClick={(e) => e.stopPropagation()}>
        <TrendChart kind="line" switchKinds={["line", "stacked-bars", "table"]} chartKey="churn-rate-by-month" percent seriesNames={["Churn rate", ""]}
          points={shown.map((t) => ({ label: t.month, a: (t.churnPct ?? 0) * 100 }))} />
      </div>
      {avg !== null && (
        <div style={legend}>
          <span style={{ marginLeft: "auto", fontWeight: 700, color: "var(--body)" }} title="Average monthly churn across the months shown">
            Avg {(avg * 100).toFixed(1)}% / month over {shown.length} mo
          </span>
        </div>
      )}
    </Card>
  );
}

/** cancel-curve — "When exactly do subscribers cancel?" (curve undefined = still loading).
 *  Clicking ENLARGES the same timing chart in a modal (founder 2026-07-06: clicking
 *  used to open a nonsensical "Cancelled subscriptions by product" drill — it should
 *  "just be a bigger version of that"). */
export function CancelCurveCard({ curve, sourceLabel = "Subscription" }: { curve: SubRetention["curve"] | undefined; sourceLabel?: string }): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const explain = [
        `<b>What it is:</b> Shows when ${sourceLabel} plans tend to cancel, using the share still active entering each week that cancel that week.`,
        `<b>How it's calculated:</b> Plan age starts on the start date recorded by ${sourceLabel}. For each week, cancellations are divided by plans still active entering it. Spikes are the top peaks above the average.`,
        "<b>Example (sample data · all plans, lifetime):</b> Week 5 = 433 cancels ÷ 5,245 still active = 8.26% (the biggest spike). Other spikes: week 9 (7.93%) and week 18 (7.24%). Total = 2,094 lifetime cancels.",
        `<b>Source limit:</b> A plan that canceled and later restarted still counts here. Weeks are capped at 52 ("1yr+"). This uses the plans returned by ${sourceLabel}.`,
      ];
  return (
    <>
      <Card style={{ marginBottom: 0 }} onClick={curve ? () => setExpanded(true) : undefined} title={curve ? "Click to enlarge" : undefined}>
        <CardHead title={`${sourceLabel}: when do plans cancel?`} explain={explain} />
        {curve ? <CancelCurve curve={curve} /> : <div style={{ color: "var(--faint)", padding: "14px 0" }}>Loading…</div>}
      </Card>
      {expanded && curve && createPortal(
        <div onClick={() => setExpanded(false)} role="dialog" aria-modal="true"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-card)", boxShadow: "var(--shadow-pop)", padding: "20px 24px", width: "min(960px, 95vw)", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{sourceLabel}: when do plans cancel?</span>
              <button onClick={() => setExpanded(false)} aria-label="Close" style={{ border: "none", background: "transparent", color: "var(--body)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>✕</button>
            </div>
            <CancelCurve curve={curve} />
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/** base-lifetime — the compact subscription economics summary. */
export function BaseLifetimeCard({
  s,
  value,
  cmAvailable = true,
  orderLinked = true,
  sourceLabel = "Subscription",
}: {
  s: SubSummary;
  value: SubResponse["subscriptionValue"];
  cmAvailable?: boolean;
  orderLinked?: boolean;
  sourceLabel?: string;
}): React.ReactElement {
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-sub-value");
  const shownMode = cmAvailable ? mode : "revenue";
  const cm = shownMode === "profit";
  const averageValue = cm ? value.avgCm12 : value.avgRevenue12;
  const captureDays = cm ? value.cmCaptureDays90 : value.revenueCaptureDays90;
  const historyCoverages = [value.rebillCountCoverage, value.linkCoverage, value.sourceValueCoverage]
    .filter((coverage): coverage is number => coverage !== null);
  const historyCoverage = historyCoverages.length > 0 ? Math.min(...historyCoverages) : null;
  const historyReady = value.cohortPlans > 0
    && value.rebillCountCoverage !== null && value.rebillCountCoverage >= 0.95
    && value.linkCoverage !== null && value.linkCoverage >= 0.95
    && (value.sourceValueCoverage === null || value.sourceValueCoverage >= 0.95);
  const costsReady = !cm || (value.cmCoverage !== null && value.cmCoverage >= 0.95);
  const historyIsEstimate = historyCoverage !== null && historyCoverage < 0.999;
  const valueIsEstimate = historyIsEstimate || (cm && value.cmEstimated);
  const historyMessage = value.cohortPlans === 0
    ? "Needs 12 months of subscription history"
    : value.rebillCountCoverage === null || value.rebillCountCoverage < 0.95
      ? "We can't verify which mature plans rebilled yet"
      : value.linkCoverage === null || value.linkCoverage < 0.95
        ? `${((value.linkCoverage ?? 0) * 100).toFixed(1)}% of plans with rebills match imported orders`
        : value.sourceValueCoverage !== null && value.sourceValueCoverage < 0.95
          ? `${(value.sourceValueCoverage * 100).toFixed(1)}% of mature plan totals match imported orders`
          : "Waiting for a complete subscription sync";
  const valueSub = !historyReady
    ? historyMessage
    : !costsReady
      ? "needs costs for the full rebill group"
      : cm && value.cmEstimated
        ? `${((value.cmCoverage ?? 0) * 100).toFixed(1)}% has exact costs · rest estimated`
        : `${historyIsEstimate ? `${(historyCoverage! * 100).toFixed(1)}% coverage · ` : ""}after signup · first 12 months · ${cm ? "CM" : "revenue"}`;
  const captureSub = !historyReady
    ? historyMessage
    : cm && value.cmEstimated
      ? "Needs complete costs to time CM collection"
    : historyIsEstimate
      ? "of linked first-year value · estimate"
      : "of the average 12-month value";
  return (
    <Card style={{ marginBottom: 0 }}>
      <CardHead title={`${sourceLabel} plan duration`} explain={orderLinked ? [
        `<b>What it is:</b> How many customers subscribe, what one new ${sourceLabel} plan is worth after signup, how long ended plans lasted, and how long the money takes to arrive.`,
        "<b>How it's calculated:</b> Plan age starts on the original sign-up order. Value and timing use plans old enough to complete a full year. Average duration uses plans that ended with known dates.",
        "<b>Note:</b> Value and collection timing appear only after plan-to-order links pass the coverage check.",
      ] : [
        `<b>What it is:</b> The average time from start to end for mature ${sourceLabel} plans with known dates.`,
        `<b>How it's calculated:</b> Average the full lifespan of ended ${sourceLabel} plans that have both a start date and an end date.`,
        "<b>Source limit:</b> Plan duration comes from plan records alone. Subscriber rate, plan value, collection timing, and contribution margin stay hidden because these plans are not reliably linked to imported orders.",
      ]}>{orderLinked ? <RevenueCmToggle mode={shownMode} setMode={setMode} cmDisabled={!cmAvailable} /> : undefined}</CardHead>
      <div style={{ display: "grid", gridTemplateColumns: orderLinked ? "1fr 1fr" : "1fr", gap: 14, marginTop: 14 }}>
        {orderLinked && <MiniStat label="% ever subscribed" value={pct(s.everSubscribedPct)} sub={s.everSubscribedPct === null && s.totalSubs > 0 && (s.everSubscribedLinkCoverage ?? 0) < 0.95 ? "Plans are not linked to customers yet" : `${s.everSubscribedCustomers.toLocaleString()} customers`} />}
        {orderLinked && <MiniStat label="Average subscription value" value={averageValue === null ? "—" : `${valueIsEstimate ? "~" : ""}${moneyFmt(averageValue)}`} sub={valueSub} />}
        <MiniStat label="Average ended-plan duration" value={value.avgStickMonths12 === null ? "—" : `${value.avgStickMonths12.toFixed(1)} months`} sub={value.avgStickMonths12 === null ? (value.endedPlans === 0 ? "No ended plans yet" : "Needs complete end dates") : `${(value.datedEndedPlans ?? value.endedPlans).toLocaleString()} of ${value.endedPlans.toLocaleString()} ended plans have known dates`} />
        {orderLinked && <MiniStat label="Time to collect 90%" value={captureDays === null ? "—" : `${valueIsEstimate ? "~" : ""}${(captureDays / 30.4375).toFixed(1)} months`} sub={captureSub} />}
      </div>
    </Card>
  );
}

/** subs-by-type — S4: split the pooled churn/value by product type. */
export function SubsByTypeCard({ rows, cmAvailable = true, style, sourceLabel = "Subscription" }: { rows: SubTypeRow[] | undefined; cmAvailable?: boolean; style?: React.CSSProperties; sourceLabel?: string }): React.ReactElement {
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-sub-type");
  const shownMode = cmAvailable ? mode : "revenue";
  const cm = shownMode === "profit";
  const shown = (rows ?? []).filter((r) => r.totalSubs > 0);
  return (
    <Card style={{ marginBottom: 0, ...style }}>
      <CardHead title={`${sourceLabel} subscriptions by type`} explain={[
        "<b>What it is:</b> Splits the program into physical subscribe-&-save vs app subscriptions, because a $9/mo app plan and a physical replenishment plan don't churn or earn anything alike — blending them would hide both.",
        "<b>How it's calculated:</b> Each plan's type and true start date come from its original sign-up order. Per type: active count, recurring value, total monthly churn from customer cancellations and failed payments, and average time to the first end event.",
        "<b>Example (sample data · current, Revenue view):</b> Physical — 3,009 active, $192,574 recurring value, 9.6% churn, 109d avg lifetime. App — 177 active, $1,593 recurring value, 10.2% churn, 109d avg lifetime. (3,009 + 177 = 3,186 active, matching the Active tile.) On the CM view: Physical recurring = $102,326; App = $846.",
        "<b>Note:</b> Type comes from the sign-up product. On the CM view, recurring CM uses each plan's product margin. If plan costs are not linked and there is no recent rebill margin to use, that row stays blank.",
      ]}><RevenueCmToggle mode={shownMode} setMode={setMode} cmDisabled={!cmAvailable} /></CardHead>
      {!rows ? <div style={{ color: "var(--faint)", padding: "14px 0" }}>Loading…</div> : shown.length === 0 ? <div style={{ color: "var(--faint)", padding: "14px 0" }}>No subscriptions yet.</div> : (
        <div style={{ overflow: "auto", marginTop: 12 }} onClick={(e) => e.stopPropagation()}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={gridTh("left")}>Type</th>
                <th style={gridTh()}>Active</th>
                <th style={gridTh()}>Recurring {cm ? "CM" : "Revenue"}</th>
                <th style={gridTh()}>Monthly churn</th>
                <th style={gridTh()}>Avg lifetime</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.type}>
                  <td style={{ ...gridTd, textAlign: "left", color: "var(--text)", fontWeight: 600, whiteSpace: "nowrap" }}>{r.label}</td>
                  <td className="dd-num" style={{ ...gridTd, color: "var(--num)" }}>{r.activeSubs.toLocaleString()}</td>
                  <td
                    className="dd-num"
                    title={cm && r.recurringValueCm === null
                      ? "Plan costs are not linked yet"
                      : cm && r.recurringValueCmEstimated
                        ? "Estimated from recent rebill margins for plans without linked costs"
                        : undefined}
                    style={{ ...gridTd, color: "var(--num)" }}
                  >
                    {cm && r.recurringValueCmEstimated ? "~" : ""}{moneyFmt(cm ? r.recurringValueCm : r.recurringValue)}
                  </td>
                  <td className="dd-num" style={{ ...gridTd, color: "var(--num)" }}>{pct(r.monthlyChurnPct)}</td>
                  <td className="dd-num" style={{ ...gridTd, color: "var(--num)" }}>{r.avgDaysToCancel === null ? "—" : `${Math.round(r.avgDaysToCancel)}d`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** sub-cohort-matrix — "Cohort retention: who's still subscribed?" (cohorts undefined = still loading). */
export function SubCohortMatrixCard({ cohorts, style, sourceLabel = "Subscription" }: { cohorts: SubRetention["cohorts"] | undefined; style?: React.CSSProperties; sourceLabel?: string }): React.ReactElement {
  return (
    <Card style={style}>
      <CardHead title={`${sourceLabel} plan retention by cohort`} explain={[
        `<b>What it is:</b> Each row is a ${sourceLabel} plan-start month. Each cell is the share of that group still active that many months later.`,
        `<b>How it's calculated:</b> Group plans by the start month recorded by ${sourceLabel}. Each cell is the share that has not ended by that point. A cell appears only after that much history exists for the whole group.`,
        "<b>Example (sample data · all plans):</b> The 2025-06 cohort (191 plans): 92% still active after 1 month, 87% after 2, 82% after 3, 63% after 9. Compare the 1-month mark across cohorts: 2025-06 = 92% vs 2026-03 = 96% — the newer cohort is holding higher.",
        `<b>Source limit:</b> Blank means ${sourceLabel} does not have enough history yet, not zero retention. The clock stops at the latest synced plan date.`,
      ]} />
      {cohorts ? <CohortGrid rows={cohorts} /> : <div style={{ color: "var(--faint)", padding: "14px 0" }}>Loading…</div>}
    </Card>
  );
}

/** Weekly cancellation-timing curve with spike callouts. */
function CancelCurve({ curve }: { curve: SubRetention["curve"] }): React.ReactElement {
  if (curve.totalCanceled === 0) return <div style={{ color: "var(--faint)", padding: "14px 0" }}>No cancellations yet.</div>;
  const max = Math.max(1, ...curve.weeks.map((w) => w.hazardPct));
  const W = 460, H = 150, padL = 8, padB = 22, padT = 14;
  const bw = (W - padL * 2) / Math.max(1, curve.weeks.length);
  // Trendline instead of columns (founder 2026-07-15): the cancel RATE per week of
  // plan life as a single line, with a dot + label on the spike weeks.
  const px = (week: number): number => padL + week * bw + bw / 2;
  const py = (hazardPct: number): number => H - padB - (hazardPct / max) * (H - padB - padT);
  const line = curve.weeks.map((w) => `${px(w.week).toFixed(1)},${py(w.hazardPct).toFixed(1)}`).join(" ");
  return (
    <div style={{ marginTop: 12 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Weekly cancel rate by week of plan life">
        <polyline points={line} fill="none" stroke="var(--bad)" strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" opacity={0.9} />
        {curve.weeks.map((w) => {
          if (!curve.spikeWeeks.includes(w.week)) return null;
          const x = px(w.week), y = py(w.hazardPct);
          return (
            <g key={w.week}>
              <circle cx={x} cy={y} r={2.7} fill="var(--bad)">
                <title>{`Week ${w.week + 1}: ${w.hazardPct.toFixed(1)}% cancel rate (${w.count.toLocaleString()} of ${w.atRisk.toLocaleString()})`}</title>
              </circle>
              <text x={x} y={y - 6} textAnchor="middle" fontSize={9} fontWeight={700} fill="var(--bad)" className="dd-num">
                {w.week >= 52 ? "1yr+" : `wk ${w.week + 1}`}
              </text>
            </g>
          );
        })}
        {[0, 13, 26, 39, 52].filter((w) => w < curve.weeks.length + 4).map((w) => (
          <text key={w} x={padL + w * bw} y={H - 7} fill="var(--body-faint)" fontSize={9}>{w === 52 ? "1yr+" : `wk ${w + 1}`}</text>
        ))}
      </svg>
      <div style={{ fontSize: 12, color: "var(--body)", marginTop: 8, lineHeight: 1.5 }}>
        {curve.spikeWeeks.length > 0 ? (
          <>Cancellations spike around <b style={{ color: "var(--text)" }}>{curve.spikeWeeks.map((w) => (w >= 52 ? "the 1-year mark" : `week ${w + 1}`)).join(", ")}</b> of a plan's life
          ({curve.totalCanceled.toLocaleString()} lifetime cancels). Schedule check-in/win-back emails just before those points.</>
        ) : (
          <>{curve.totalCanceled.toLocaleString()} lifetime cancellations, spread without a sharp spike.</>
        )}
      </div>
    </div>
  );
}

/** Start-month cohort × months-since-start retention grid. */
function CohortGrid({ rows }: { rows: SubRetention["cohorts"] }): React.ReactElement {
  if (rows.length === 0) return <div style={{ color: "var(--faint)", padding: "14px 0" }}>No subscription cohorts yet.</div>;
  return (
    <div style={{ overflow: "auto", marginTop: 12 }}>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
        <thead>
          <tr>
            <th style={gridTh("left")}>Started</th>
            <th style={gridTh()}>Plans</th>
            {Array.from({ length: 12 }, (_, i) => <th key={i} style={gridTh()}>M{i + 1}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.cohort}>
              <td style={{ ...gridTd, textAlign: "left", color: "var(--body)", whiteSpace: "nowrap" }}>{r.cohort}</td>
              <td className="dd-num" style={{ ...gridTd, color: "var(--num)" }}>{r.size.toLocaleString()}</td>
              {r.activePctByMonth.map((v, i) => (
                <td key={i} style={{ padding: 2 }}>
                  {v === null ? (
                    <div style={{ borderRadius: 5, padding: "6px 2px", textAlign: "center", color: "var(--faint)" }}>—</div>
                  ) : (
                    <div className="dd-num" title={`${v.toFixed(1)}% still active after ${i + 1} months`}
                      style={{ borderRadius: 5, padding: "6px 2px", textAlign: "center", color: v > 55 ? "var(--accent-contrast)" : "var(--text)", background: `color-mix(in srgb, var(--accent) ${Math.round(v * 0.85)}%, var(--surface-2))` }}>
                      {Math.round(v)}%
                    </div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const gridTh = (align: "left" | "right" | "center" = "center"): React.CSSProperties => ({ textAlign: align, padding: "7px 6px", color: "var(--faint)", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" });
const gridTd: React.CSSProperties = { padding: "6px 6px", textAlign: "right", borderBottom: "1px solid var(--hair)" };

function MiniStat({ label, value, sub, onClick }: { label: string; value: string; sub: string; onClick?: () => void }): React.ReactElement {
  return (
    <div onClick={onClick} className={onClick ? "dd-drill-row" : undefined} title={onClick ? "Click to explore" : undefined}
      style={onClick ? { cursor: "pointer", borderRadius: 8, padding: 4, margin: -4 } : undefined}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--faint)" }}>{label}</div>
      <div className="dd-num" style={{ fontSize: 20, fontWeight: 700, color: "var(--num)", marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: "var(--body)", marginTop: 2 }}>{sub}</div>
    </div>
  );
}

const legend: React.CSSProperties = { display: "flex", gap: 14, marginTop: 10, color: "var(--body)", fontSize: 12 };
const dot = (bg: string): React.CSSProperties => ({ display: "inline-block", width: 10, height: 10, borderRadius: 3, marginRight: 5, verticalAlign: -1, background: bg });

/* ───────────────────────── no-prop preview wrappers ──────────────────────── */

function Pending(): React.ReactElement {
  return <div style={{ color: "var(--faint)", fontSize: 13, padding: "14px 0" }}>Loading…</div>;
}

function SubMoney30dPreview(): React.ReactElement {
  const { data } = useSubsQuery();
  if (!data?.sourceCoverage) return <Pending />;
  return <RecurringSalesCard rb={data.rebills} coverage={data.sourceCoverage.recurringSales} />;
}

function UpcomingChargesPreview(): React.ReactElement {
  const { data } = useSubsQuery();
  if (!data?.sourceCoverage) return <Pending />;
  return <UpcomingChargesCard s={data.summary} coverage={data.sourceCoverage.scheduledDates} sourceLabel={subscriptionPlanSourceLabel(data.sourceCoverage)} />;
}

function SubscriptionProjectionPreview(): React.ReactElement {
  const { data } = useSubsQuery();
  const source = data?.sourceCoverage;
  if (!data || !source || !source.orderLinkage.reliable || !source.forecastCompatible || data.rebills.projected30 === null) return <Pending />;
  return <SubscriptionProjectionCard rb={data.rebills} sourceLabel={subscriptionPlanSourceLabel(source)} />;
}

function NetAddsPreview(): React.ReactElement {
  const { data } = useSubsQuery();
  if (!data) return <Pending />;
  return <NetAddsTile s={data.summary} sourceLabel={subscriptionPlanSourceLabel(data.sourceCoverage)} />;
}

function MonthlyChurnPreview(): React.ReactElement {
  const { data } = useSubsQuery();
  if (!data) return <Pending />;
  return <MonthlyChurnTile s={data.summary} sourceLabel={subscriptionPlanSourceLabel(data.sourceCoverage)} />;
}

function ChurnRateByMonthPreview(): React.ReactElement {
  const { data } = useSubsQuery();
  if (!data) return <Pending />;
  return <ChurnRateByMonthCard churnTrend={data.churnTrend} sourceLabel={subscriptionPlanSourceLabel(data.sourceCoverage)} />;
}

function ActiveSubsPreview(): React.ReactElement {
  const { data } = useSubsQuery();
  if (!data) return <Pending />;
  return <ActiveSubsTile s={data.summary} sourceLabel={subscriptionPlanSourceLabel(data.sourceCoverage)} />;
}

function SubscriptionLeaksPreview(): React.ReactElement {
  const { data } = useSubsQuery();
  if (!data) return <Pending />;
  const linked = data.sourceCoverage?.orderLinkage.reliable === true;
  return <SubscriptionLeaksCard leaks={data.leaks} cmAvailable={linked && data.hasCm !== false} sourceLabel={subscriptionPlanSourceLabel(data.sourceCoverage)} />;
}

function SubsNewVsCanceledPreview(): React.ReactElement {
  const { data } = useSubsQuery();
  if (!data) return <Pending />;
  return <SubsNewVsCanceledCard trend={data.trend} sourceLabel={subscriptionPlanSourceLabel(data.sourceCoverage)} />;
}

function CancelCurvePreview(): React.ReactElement {
  const { data } = useSubsQuery();
  const retention = useSubRetentionQuery();
  return <CancelCurveCard curve={retention.data?.curve} sourceLabel={subscriptionPlanSourceLabel(data?.sourceCoverage)} />;
}

function BaseLifetimePreview(): React.ReactElement {
  const { data } = useSubsQuery();
  if (!data) return <Pending />;
  const linked = data.sourceCoverage?.orderLinkage.reliable === true;
  if (!linked) return <Pending />;
  return <BaseLifetimeCard s={data.summary} value={data.subscriptionValue} cmAvailable={data.hasCm !== false} orderLinked sourceLabel={subscriptionPlanSourceLabel(data.sourceCoverage)} />;
}

function PlanDurationPreview(): React.ReactElement {
  const { data } = useSubsQuery();
  if (!data) return <Pending />;
  return <BaseLifetimeCard s={data.summary} value={data.subscriptionValue} cmAvailable={false} orderLinked={false} sourceLabel={subscriptionPlanSourceLabel(data.sourceCoverage)} />;
}

function SubsByTypePreview(): React.ReactElement {
  const { data } = useSubsQuery();
  if (!data) return <Pending />;
  return <SubsByTypeCard rows={data.byType} cmAvailable={data.hasCm !== false} sourceLabel={subscriptionPlanSourceLabel(data.sourceCoverage)} />;
}

function SubCohortMatrixPreview(): React.ReactElement {
  const { data } = useSubsQuery();
  const retention = useSubRetentionQuery();
  return <SubCohortMatrixCard cohorts={retention.data?.cohorts} sourceLabel={subscriptionPlanSourceLabel(data?.sourceCoverage)} />;
}

export const SUBSCRIPTIONS_PREVIEWS: Record<string, React.ComponentType> = {
  "sub-money-30d": SubMoney30dPreview,
  "subscription-upcoming-charges": UpcomingChargesPreview,
  "subscription-projection": SubscriptionProjectionPreview,
  "net-adds": NetAddsPreview,
  "monthly-churn": MonthlyChurnPreview,
  "active-subs": ActiveSubsPreview,
  "subscription-leaks": SubscriptionLeaksPreview,
  "subs-new-vs-canceled": SubsNewVsCanceledPreview,
  "churn-rate-by-month": ChurnRateByMonthPreview,
  "cancel-curve": CancelCurvePreview,
  "subscription-plan-duration": PlanDurationPreview,
  "base-lifetime": BaseLifetimePreview,
  "subs-by-type": SubsByTypePreview,
  "sub-cohort-matrix": SubCohortMatrixPreview,
};
